import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './AdmissionsPage.css';
import defaultOnlineImage from './assets/OnlineApplication_Students.png';

const API_BASE = (() => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'https://permataharapansecondary.sch.id/api';
  }
  return `${protocol}//${hostname}/api`;
})();

const isVideoUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  const clean = url.split('?')[0].toLowerCase();
  return clean.endsWith('.mp4') || clean.endsWith('.webm') || clean.endsWith('.mov') ||
         url.includes('youtube.com') || url.includes('youtu.be');
};

const extractYouTubeId = (url) => {
  if (!url || typeof url !== 'string') return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
};

const ensureArray = (val) => {
  if (Array.isArray(val)) return val.filter(Boolean);
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (!trimmed) return [];
    if (trimmed.includes('\n')) {
      return trimmed.split(/\r?\n/).map(s => s.trim().replace(/^[•\-*]\s*/, '')).filter(Boolean);
    }
    return [trimmed];
  }
  return [];
};

function AdmissionsPage({ page }) {
  // Media map for resolving image IDs and relative URLs
  const [mediaMap, setMediaMap] = useState(() => {
    try {
      const cached = sessionStorage.getItem('cms_media_admissions');
      return cached ? JSON.parse(cached) : {};
    } catch (e) {
      return {};
    }
  });

  useEffect(() => {
    const loadMedia = async () => {
      try {
        const res = await fetch(`${API_BASE}/admissions/cms/media`, { credentials: 'omit' });
        if (res.ok) {
          const json = await res.json();
          const items = Array.isArray(json.data) ? json.data : [];
          const map = {};
          items.forEach(m => {
            if (m.id && m.url) {
              map[m.id] = m.url;
              map[String(m.id)] = m.url;
            }
            if (m.file_name && m.url) {
              map[m.file_name] = m.url;
            }
            if (m.file_path && m.url) {
              map[m.file_path] = m.url;
            }
          });
          if (Object.keys(map).length > 0) {
            setMediaMap(map);
            try { sessionStorage.setItem('cms_media_admissions', JSON.stringify(map)); } catch (_) {}
          }
        }
      } catch (e) {
        console.warn('[AdmissionsPage] Media fetch error:', e);
      }
    };
    loadMedia();
  }, []);

  // Live sections fetch to guarantee 100% database published content with zero lag
  const [liveSections, setLiveSections] = useState(() => page?.raw_sections || page?.sections || {});

  useEffect(() => {
    const fetchSections = async () => {
      try {
        const res = await fetch(`${API_BASE}/admissions/cms/sections?published=true&_t=${Date.now()}`, { credentials: 'omit' });
        if (res.ok) {
          const json = await res.json();
          const items = Array.isArray(json.data) ? json.data : [];
          const map = {};
          items.forEach(sec => {
            const cp = (sec.content_published && sec.content_published !== '{}' && sec.content_published !== '[]')
              ? sec.content_published
              : (sec.content || sec.content_published);
            let parsed = null;
            try { parsed = typeof cp === 'string' ? JSON.parse(cp) : cp; } catch (e) { parsed = cp; }
            if (parsed) {
              map[sec.section_type] = parsed.en || parsed;
            }
          });
          if (Object.keys(map).length > 0) {
            setLiveSections(map);
          }
        }
      } catch (err) {
        console.warn('[AdmissionsPage] Live sections fetch error:', err);
      }
    };
    fetchSections();
  }, []);

  const resolveMedia = useCallback((idOrPath, fallback = '') => {
    if (!idOrPath) return fallback;
    if (mediaMap[idOrPath]) return mediaMap[idOrPath];
    if (mediaMap[String(idOrPath)]) return mediaMap[String(idOrPath)];
    const str = String(idOrPath).trim();
    if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('blob:') || str.startsWith('data:')) {
      return str;
    }
    const apiOrigin = API_BASE.replace(/\/api\/?$/, '');
    if (str.startsWith('/')) {
      const path = (str.startsWith('/api/') || str === '/api') ? str : `/api${str}`;
      return `${apiOrigin}${path}`;
    }
    if (str.startsWith('uploads/') || str.startsWith('api/')) {
      const path = str.startsWith('api/') ? `/${str}` : `/api/${str}`;
      return `${apiOrigin}${path}`;
    }
    return fallback;
  }, [mediaMap]);

  // Section references from database content_published
  const heroSec = liveSections['admissions-hero'] || page?.raw_sections?.['admissions-hero'] || page?.sections?.['admissions-hero'] || {};
  const levelsSec = liveSections['admissions-levels'] || page?.raw_sections?.['admissions-levels'] || page?.sections?.['admissions-levels'] || {};
  const procSec = liveSections['admissions-process'] || page?.raw_sections?.['admissions-process'] || page?.sections?.['admissions-process'] || {};
  const reqSec = liveSections['admissions-requirements'] || page?.raw_sections?.['admissions-requirements'] || page?.sections?.['admissions-requirements'] || {};
  const datesSec = liveSections['admissions-key-dates'] || page?.raw_sections?.['admissions-key-dates'] || page?.sections?.['admissions-key-dates'] || {};
  const feesSec = liveSections['admissions-fees-payment'] || page?.raw_sections?.['admissions-fees-payment'] || page?.sections?.['admissions-fees-payment'] || {};
  const onlineSec = liveSections['admissions-online-application'] || page?.raw_sections?.['admissions-online-application'] || page?.sections?.['admissions-online-application'] || {};

  // -------------------------------------------------------------------------
  // 1. HERO SECTION DATA
  // -------------------------------------------------------------------------
  const heroKicker = heroSec.kicker || page?.hero_kicker || 'Admissions';
  const heroTitle = heroSec.title || page?.hero_title || page?.title || 'Admissions';
  const heroSummary = heroSec.summary || heroSec.subtitle || page?.hero_summary || page?.summary || '';

  const rawSlides = useMemo(() => {
    if (Array.isArray(heroSec.slides) && heroSec.slides.length > 0) return heroSec.slides;
    if (Array.isArray(page?.hero_slides) && page.hero_slides.length > 0) return page.hero_slides;
    if (Array.isArray(page?.slides) && page.slides.length > 0) return page.slides;
    return [];
  }, [heroSec.slides, page?.hero_slides, page?.slides]);

  const heroSlides = useMemo(() => {
    const list = rawSlides.map((slide, idx) => {
      const rawUrl = typeof slide === 'object' && slide !== null
        ? (slide.image || slide.url || slide.video || '')
        : (typeof slide === 'string' ? slide : '');
      const resolved = resolveMedia(rawUrl);
      const videoCandidate = (typeof slide === 'object' && slide !== null && slide.video)
        ? resolveMedia(slide.video)
        : (isVideoUrl(resolved) ? resolved : null);
      const isVideo = Boolean(videoCandidate);
      const youtubeId = isVideo ? extractYouTubeId(videoCandidate) : null;
      return {
        id: (typeof slide === 'object' && slide?.order) ? slide.order : idx,
        url: videoCandidate || resolved,
        isVideo,
        isYouTube: Boolean(youtubeId),
        youtubeId
      };
    }).filter(s => Boolean(s.url));

    const singleVideo = heroSec.video || heroSec.video_url || heroSec.video_file || page?.hero_video;
    if (list.length === 0 && singleVideo) {
      const resolvedVid = resolveMedia(singleVideo);
      const ytId = extractYouTubeId(resolvedVid);
      list.push({
        id: 'hero-vid-0',
        url: resolvedVid,
        isVideo: true,
        isYouTube: Boolean(ytId),
        youtubeId: ytId
      });
    }

    return list;
  }, [rawSlides, resolveMedia, heroSec.video, heroSec.video_url, heroSec.video_file, page?.hero_video]);

  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    if (heroSlides.length <= 1) return;
    const intervalId = setInterval(() => {
      setActiveSlide((current) => (current + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(intervalId);
  }, [heroSlides.length]);

  useEffect(() => {
    const animatedItems = document.querySelectorAll('.adm-animate');
    if (!animatedItems.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    animatedItems.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, []);

  // -------------------------------------------------------------------------
  // 2. ADMISSIONS LEVELS DATA (100% Database Driven)
  // -------------------------------------------------------------------------
  const levelsKicker = levelsSec.subtitle || page?.levels_subtitle || 'Admissions Pathways';
  const levelsTitle = levelsSec.title || page?.levels_title || 'Tailored Admissions for Every Stage';
  const levelsDescription = levelsSec.description || page?.levels_description || '';

  const admissionsLevels = useMemo(() => {
    const raw = levelsSec.levels || page?.admissions_levels || page?.levels;
    if (!Array.isArray(raw)) return [];
    return raw.filter(lvl => {
      if (!lvl || typeof lvl !== 'object') return false;
      if (lvl.delete !== undefined || lvl.deleted === true || lvl._deleted === true) return false;
      const title = (lvl.title || '').trim().toLowerCase();
      if (!title || title === 'xxx') return false;
      return true;
    }).map((lvl) => ({
      ...lvl,
      image: resolveMedia(lvl.image || lvl.photo || lvl.url),
      highlights: ensureArray(lvl.highlights),
      outcomes: ensureArray(lvl.outcomes),
    }));
  }, [levelsSec.levels, page?.admissions_levels, page?.levels, resolveMedia]);

  // -------------------------------------------------------------------------
  // 3. ADMISSIONS PROCESS DATA (100% Database Driven)
  // -------------------------------------------------------------------------
  const processKicker = procSec.subtitle || page?.process_kicker || 'Admissions Process';
  const processTitle = procSec.title || page?.process_title || 'Your Child\'s Journey Starts Here';
  const processDescription = procSec.description || page?.process_description || '';

  const processSteps = useMemo(() => {
    const raw = procSec.steps || page?.process_steps || page?.steps;
    if (!Array.isArray(raw)) return [];
    return raw.filter(step => {
      if (!step || typeof step !== 'object') return false;
      if (step.delete !== undefined || step.deleted === true || step._deleted === true) return false;
      const title = (step.title || '').trim().toLowerCase();
      if (!title || title === 'xxx') return false;
      return true;
    }).map((step) => ({
      ...step,
      image: resolveMedia(step.image || step.photo || step.url),
    }));
  }, [procSec.steps, page?.process_steps, page?.steps, resolveMedia]);

  // -------------------------------------------------------------------------
  // 4. REQUIREMENTS DATA (100% Database Driven)
  // -------------------------------------------------------------------------
  const requirementsKicker = reqSec.subtitle || page?.requirements_kicker || 'Requirements';
  const requirementsTitle = reqSec.title || page?.requirements_title || 'Documents & Eligibility by Level';
  const requirementsDescription = reqSec.description || page?.requirements_description || '';

  const requirementsList = useMemo(() => {
    const raw = reqSec.requirements || page?.requirements;
    if (!Array.isArray(raw)) return [];
    return raw.filter(r => {
      if (!r || typeof r !== 'object') return false;
      if (r.delete !== undefined || r.deleted === true || r._deleted === true) return false;
      const lvl = (r.level || r.title || '').trim().toLowerCase();
      if (!lvl || lvl === 'xxx') return false;
      return true;
    }).map(r => ({
      level: r.level || r.title || 'Level',
      description: r.description || '',
      documents: ensureArray(r.documents),
      eligibility: ensureArray(r.eligibility || r.criteria),
    }));
  }, [reqSec.requirements, page?.requirements]);

  // -------------------------------------------------------------------------
  // 5. KEY DATES DATA (100% Database Driven)
  // -------------------------------------------------------------------------
  const datesKicker = datesSec.subtitle || page?.key_dates_kicker || 'Timeline';
  const datesTitle = datesSec.title || page?.key_dates_title || 'Admissions Timeline & Key Dates';
  const datesDescription = datesSec.description || page?.key_dates_description || '';

  const keyDatesList = useMemo(() => {
    const raw = datesSec.dates || page?.key_dates;
    if (!Array.isArray(raw)) return [];
    return raw.filter(d => {
      if (!d || typeof d !== 'object') return false;
      if (d.delete !== undefined || d.deleted === true || d._deleted === true) return false;
      const title = (d.title || '').trim().toLowerCase();
      if (!title || title === 'xxx') return false;
      return true;
    }).map(d => {
      const displayDate = d.date || d.dateRange || d.date_range || (d.dateStart ? (d.dateEnd && d.dateEnd !== d.dateStart ? `${d.dateStart} to ${d.dateEnd}` : d.dateStart) : '');
      return {
        ...d,
        date: displayDate,
        dateRange: d.dateRange || displayDate
      };
    });
  }, [datesSec.dates, page?.key_dates]);

  // -------------------------------------------------------------------------
  // 6. FEES & PAYMENT DATA (100% Database Driven)
  // -------------------------------------------------------------------------
  const feesKicker = feesSec.subtitle || page?.fees_kicker || 'Fees & Payment Information';
  const feesTitle = feesSec.title || page?.fees_title || 'Transparent Fees, Flexible Plans & Clear Payment Options';
  const feesDescription = feesSec.description || page?.fees_description || '';

  const feesPaymentList = useMemo(() => {
    const raw = feesSec.blocks || page?.fees_payment;
    if (!Array.isArray(raw)) return [];
    return raw.filter(b => {
      if (!b || typeof b !== 'object') return false;
      if (b.delete !== undefined || b.deleted === true || b._deleted === true) return false;
      const title = (b.title || '').trim().toLowerCase();
      if (!title || title === 'xxx') return false;
      return true;
    }).map(block => ({
      ...block,
      items: ensureArray(block.items)
    }));
  }, [feesSec.blocks, page?.fees_payment]);

  // -------------------------------------------------------------------------
  // 7. ONLINE APPLICATION DATA (100% Database Driven)
  // -------------------------------------------------------------------------
  const onlineData = (typeof onlineSec.onlineApplication === 'object' && onlineSec.onlineApplication !== null) ? onlineSec.onlineApplication : {};
  const onlineKicker = onlineSec.subtitle || onlineData.subtitle || page?.online_application_kicker || 'Online Application';
  const onlineTitle = onlineSec.title || onlineData.title || page?.online_application_title || 'Online Application Form';
  const onlineSummary = onlineSec.summary || onlineData.summary || page?.online_application_summary || '';
  const onlineCtaLabel = onlineSec.ctaLabel || onlineSec.cta_label || onlineData.ctaLabel || onlineData.cta_label || 'Apply Online Now';
  const onlineCtaHref = onlineSec.ctaHref || onlineSec.cta_href || onlineData.ctaHref || onlineData.cta_href || '/register';
  const onlineNote = onlineSec.note || onlineData.note || '';
  const onlineImage = resolveMedia(onlineSec.image || onlineData.image);
  const onlineHighlights = ensureArray(onlineSec.highlights || onlineData.highlights);

  return (
    <section className="admissions-page-shell">
      {/* Redesigned Hero Section - Matches About Us Hero Exactly */}
      <section className="admissions-hero" id="admissions-overview">
        <div className="admissions-hero-media" aria-hidden="true">
          {heroSlides.length > 0 ? (
            heroSlides.map((slide, index) => {
              const isCurrent = index === activeSlide;
              return (
                <div
                  key={slide.id || index}
                  className={`admissions-hero-slide ${isCurrent ? 'active' : ''}`}
                >
                  {slide.isVideo ? (
                    slide.isYouTube ? (
                      <iframe
                        className="admissions-hero-video-frame"
                        src={`https://www.youtube.com/embed/${slide.youtubeId}?autoplay=1&mute=1&loop=1&playlist=${slide.youtubeId}&controls=0&playsinline=1&modestbranding=1&rel=0`}
                        title="Admissions hero video background"
                        allow="autoplay; encrypted-media"
                        tabIndex="-1"
                      />
                    ) : (
                      <video
                        className="admissions-hero-video-element"
                        src={slide.url}
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="auto"
                      />
                    )
                  ) : (
                    <img
                      src={slide.url}
                      alt={heroTitle}
                      className="admissions-hero-img-element"
                    />
                  )}
                </div>
              );
            })
          ) : (
            <div className="admissions-hero-slide active admissions-hero-gradient-fallback" />
          )}
        </div>

        <div className="admissions-hero-overlay" aria-hidden="true" />

        <div className="admissions-hero-content-wrap">
          <div className="admissions-hero-content">
            <p className="admissions-hero-kicker">
              {heroKicker}
            </p>

            <h1 className="admissions-hero-title">
              {heroTitle}
            </h1>

            {heroSummary && (
              <p className="admissions-hero-summary">
                {heroSummary}
              </p>
            )}

            {heroSlides.length > 1 && (
              <div className="admissions-hero-dots" role="tablist" aria-label="Hero slides navigation">
                {heroSlides.map((slide, index) => (
                  <button
                    key={slide.id || index}
                    type="button"
                    className={`admissions-hero-dot ${index === activeSlide ? 'active' : ''}`}
                    onClick={() => setActiveSlide(index)}
                    aria-label={`Slide ${index + 1}`}
                    role="tab"
                    aria-selected={index === activeSlide}
                  >
                    <span className="admissions-hero-dot-bar" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Admissions Levels Section */}
      {admissionsLevels.length > 0 && (
        <section className="admissions-levels" id="admissions-levels">
          <div className="admissions-levels-header">
            <div className="admissions-levels-kicker-wrap">
              <span className="admissions-levels-kicker-badge">
                <span className="admissions-levels-kicker-dot" aria-hidden="true" />
                {levelsKicker}
              </span>
            </div>
            <h3 className="admissions-levels-title">{levelsTitle}</h3>
            {levelsDescription && <p className="admissions-levels-description">{levelsDescription}</p>}
          </div>

          <AdmissionsLevelsCarousel levels={admissionsLevels} />
        </section>
      )}

      {/* Admissions Process Section */}
      {processSteps.length > 0 && (
        <section className="admissions-process admissions-process-steps" id="admissions-process">
          <div className="admissions-process-header">
            <div className="admissions-process-kicker-wrap">
              <span className="admissions-process-kicker-badge">
                <span className="admissions-process-kicker-dot" aria-hidden="true" />
                {processKicker}
              </span>
            </div>
            <h3 className="admissions-process-title">{processTitle}</h3>
            {processDescription && <p className="admissions-process-description">{processDescription}</p>}
          </div>

          <AdmissionsProcessSteps steps={processSteps} />
        </section>
      )}

      {/* Requirements Section */}
      {requirementsList.length > 0 && (
        <section className="admissions-requirements" id="admissions-requirements">
          <div className="admissions-requirements-header">
            <div className="admissions-requirements-kicker-wrap">
              <span className="admissions-requirements-kicker-badge">
                <span className="admissions-requirements-kicker-dot" aria-hidden="true" />
                {requirementsKicker}
              </span>
            </div>
            <h3 className="admissions-requirements-title">{requirementsTitle}</h3>
            {requirementsDescription && (
              <p className="admissions-requirements-description">{requirementsDescription}</p>
            )}
          </div>

          <AdmissionsRequirementsSection requirements={requirementsList} />
        </section>
      )}

      {/* Key Dates Timeline Section */}
      {keyDatesList.length > 0 && (
        <AdmissionsKeyDatesTimeline
          keyDates={keyDatesList}
          header={{ kicker: datesKicker, title: datesTitle, description: datesDescription }}
        />
      )}

      {/* Fees & Payment Section */}
      {feesPaymentList.length > 0 && (
        <AdmissionsFeesPaymentSection
          feesList={feesPaymentList}
          header={{ kicker: feesKicker, title: feesTitle, description: feesDescription }}
        />
      )}

      {/* Online Application Section */}
      {(onlineTitle || onlineSummary || onlineImage || onlineCtaLabel) && (
        <AdmissionsOnlineApplicationSection
          data={{
            kicker: onlineKicker,
            title: onlineTitle,
            summary: onlineSummary,
            ctaLabel: onlineCtaLabel,
            ctaHref: onlineCtaHref,
            note: onlineNote,
            image: onlineImage,
            highlights: onlineHighlights,
          }}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// SUB-COMPONENTS (100% Dynamic, Zero Hardcoded Data)
// ---------------------------------------------------------------------------

function AdmissionsLevelsCarousel({ levels }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);

  if (!levels || levels.length === 0) return null;

  const handlePrev = () => {
    setActiveIndex((curr) => (curr - 1 + levels.length) % levels.length);
  };

  const handleNext = () => {
    setActiveIndex((curr) => (curr + 1) % levels.length);
  };

  const handleTouchStart = (e) => {
    if (e.touches && e.touches.length > 0) {
      setTouchStartX(e.touches[0].clientX);
      setTouchEndX(null);
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches && e.touches.length > 0) {
      setTouchEndX(e.touches[0].clientX);
    }
  };

  const handleTouchEnd = () => {
    if (touchStartX === null || touchEndX === null) return;
    const diff = touchStartX - touchEndX;
    if (diff > 45) {
      handleNext();
    } else if (diff < -45) {
      handlePrev();
    }
    setTouchStartX(null);
    setTouchEndX(null);
  };

  return (
    <div className="admissions-slider-container">
      {/* Floating Side Arrow Buttons (Desktop & Tablet) */}
      {levels.length > 1 && (
        <>
          <button
            type="button"
            className="admissions-floating-arrow prev"
            onClick={handlePrev}
            aria-label="Previous level"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            type="button"
            className="admissions-floating-arrow next"
            onClick={handleNext}
            aria-label="Next level"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </>
      )}

      {/* Slider Viewport with Touch Swiping */}
      <div
        className="admissions-slider-viewport"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="admissions-slider-track"
          style={{ transform: `translateX(-${activeIndex * 100}%)` }}
        >
          {levels.map((level, idx) => {
            const stageNum = String(idx + 1).padStart(2, '0');
            const pathwaySteps = typeof level.process === 'string'
              ? level.process.split('→').map((step) => step.trim()).filter(Boolean)
              : (Array.isArray(level.process) ? level.process : []);

            return (
              <div
                key={level.title || idx}
                className={`admissions-slider-slide ${idx === activeIndex ? 'active' : ''}`}
                aria-hidden={idx !== activeIndex}
              >
                <article className="admissions-level-master-card">
                  <div className="admissions-card-top-ribbon" aria-hidden="true" />

                  <div className="admissions-card-grid">
                    {/* Left Column: Media & Quick Facts */}
                    <div className="admissions-card-media-col">
                      {level.image ? (
                        <div className="admissions-card-media-frame">
                          <img
                            src={level.image}
                            alt={level.title}
                            className="admissions-card-img"
                            loading="lazy"
                          />
                          <div className="admissions-card-media-overlay" aria-hidden="true" />
                          <div className="admissions-card-media-badge">
                            <span>Stage {stageNum}</span>
                            {level.ageRange && <span>• {level.ageRange}</span>}
                          </div>
                        </div>
                      ) : (
                        <div className="admissions-card-media-frame placeholder">
                          <div className="admissions-card-media-overlay" aria-hidden="true" />
                          <div className="admissions-card-media-badge">
                            <span>Stage {stageNum}</span>
                            {level.ageRange && <span>• {level.ageRange}</span>}
                          </div>
                        </div>
                      )}

                      {/* Quick Facts Grid */}
                      <div className="admissions-quick-facts-grid">
                        {level.intake && (
                          <div className="admissions-fact-box">
                            <div className="fact-box-header">
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                              </svg>
                              <span>Intake Window</span>
                            </div>
                            <p>{level.intake}</p>
                          </div>
                        )}

                        {level.timeline && (
                          <div className="admissions-fact-box">
                            <div className="fact-box-header">
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" />
                                <polyline points="12 6 12 12 16 14" />
                              </svg>
                              <span>Timeline</span>
                            </div>
                            <p>{level.timeline}</p>
                          </div>
                        )}

                        {level.assessment && (
                          <div className="admissions-fact-box full-width">
                            <div className="fact-box-header">
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                                <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                              </svg>
                              <span>Assessment Approach</span>
                            </div>
                            <p>{level.assessment}</p>
                          </div>
                        )}

                        {level.support && (
                          <div className="admissions-fact-box full-width">
                            <div className="fact-box-header">
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                <circle cx="9" cy="7" r="4" />
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                              </svg>
                              <span>Family Support</span>
                            </div>
                            <p>{level.support}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right Column: Details, Pathway, Checkpoints, Outcomes, CTA */}
                    <div className="admissions-card-info-col">
                      <div className="admissions-card-header-row">
                        <div className="header-row-badges">
                          <span className="admissions-stage-pill">Stage {stageNum}</span>
                          {level.note && (
                            <span className="admissions-note-badge">{level.note}</span>
                          )}
                          {level.ageRange && (
                            <span className="admissions-age-badge">{level.ageRange}</span>
                          )}
                        </div>
                        <h4 className="admissions-showcase-title">{level.title}</h4>
                      </div>

                      {level.description && (
                        <p className="admissions-showcase-description">{level.description}</p>
                      )}

                      {/* Admission Pathway Visual Journey */}
                      {pathwaySteps.length > 0 && (
                        <div className="admissions-pathway-journey">
                          <span className="pathway-journey-label">Admission Journey</span>
                          <div className="pathway-steps-flow">
                            {pathwaySteps.map((step, sIdx) => (
                              <React.Fragment key={sIdx}>
                                <span className="pathway-flow-step">
                                  <span className="step-idx">{sIdx + 1}</span>
                                  <span>{step}</span>
                                </span>
                                {sIdx < pathwaySteps.length - 1 && (
                                  <svg className="step-arrow-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <polyline points="9 18 15 12 9 6" />
                                  </svg>
                                )}
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Dual Checkpoints & Outcomes Lists */}
                      <div className="admissions-features-dual-grid">
                        {level.highlights && level.highlights.length > 0 && (
                          <div className="admissions-feature-block">
                            <h5 className="feature-block-heading">
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                              <span>Key Checkpoints</span>
                            </h5>
                            <ul className="feature-list">
                              {level.highlights.map((highlight, hIdx) => (
                                <li key={hIdx}>
                                  <span className="feature-bullet check">✓</span>
                                  <span>{highlight}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {level.outcomes && level.outcomes.length > 0 && (
                          <div className="admissions-feature-block">
                            <h5 className="feature-block-heading">
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                              </svg>
                              <span>Expected Outcomes</span>
                            </h5>
                            <ul className="feature-list">
                              {level.outcomes.map((outcome, oIdx) => (
                                <li key={oIdx}>
                                  <span className="feature-bullet star">★</span>
                                  <span>{outcome}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      {/* Card Footer Inquiry Action */}
                      <div className="admissions-card-footer-stepper">
                        <span className="stepper-stage-info">
                          Stage {stageNum} of {String(levels.length).padStart(2, '0')}
                        </span>
                        <a className="admissions-inquire-link" href="#online-application">
                          <span>Inquire for {level.title.replace(/\s*admissions\s*$/i, '')}</span>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <line x1="5" y1="12" x2="19" y2="12" />
                            <polyline points="12 5 19 12 12 19" />
                          </svg>
                        </a>
                      </div>
                    </div>
                  </div>
                </article>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bottom Slider Navigation Bar with Arrow Buttons & Expanding Indicator Dots */}
      {levels.length > 1 && (
        <div className="admissions-slider-bottom-bar">
          <button
            type="button"
            className="admissions-slider-arrow-btn prev"
            onClick={handlePrev}
            aria-label="Previous pathway level"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            <span className="slider-arrow-label">Prev Stage</span>
          </button>

          <div className="admissions-slider-dots-group">
            {levels.map((lvl, dotIdx) => (
              <button
                key={dotIdx}
                type="button"
                className={`admissions-slider-dot ${dotIdx === activeIndex ? 'active' : ''}`}
                onClick={() => setActiveIndex(dotIdx)}
                aria-label={`Slide to stage ${dotIdx + 1}`}
              >
                <span className="dot-pill-shape" />
              </button>
            ))}
            <span className="admissions-slider-counter-badge">
              Stage {activeIndex + 1} / {levels.length}
            </span>
          </div>

          <button
            type="button"
            className="admissions-slider-arrow-btn next"
            onClick={handleNext}
            aria-label="Next pathway level"
          >
            <span className="slider-arrow-label">Next Stage</span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

function AdmissionsProcessSteps({ steps }) {
  const trackRef = useRef(null);
  const [activeStep, setActiveStep] = useState(0);
  const [tappedStep, setTappedStep] = useState(null);

  if (!steps || steps.length === 0) return null;

  const scrollToStep = (index) => {
    const clampedIndex = Math.max(0, Math.min(steps.length - 1, index));
    setActiveStep(clampedIndex);
    const track = trackRef.current;
    const panel = track?.children?.[clampedIndex];
    if (panel && track) {
      const trackRect = track.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();
      const scrollOffset = panelRect.left - trackRect.left + track.scrollLeft;
      track.scrollTo({ left: scrollOffset, behavior: 'smooth' });
    }
  };

  const handleTrackScroll = () => {
    const track = trackRef.current;
    if (!track || !track.children.length) return;
    const scrollLeft = track.scrollLeft;
    const panelWidth = track.children[0]?.offsetWidth || 1;
    const calculatedIndex = Math.round(scrollLeft / panelWidth);
    const clamped = Math.max(0, Math.min(steps.length - 1, calculatedIndex));
    if (clamped !== activeStep) {
      setActiveStep(clamped);
    }
  };

  const handlePanelClick = (index) => {
    setTappedStep((current) => (current === index ? null : index));
    scrollToStep(index);
  };

  const handlePanelMouseLeave = () => {
    setTappedStep(null);
  };

  return (
    <div className="admissions-carousel-shell">
      <div
        className="admissions-carousel-track"
        ref={trackRef}
        onScroll={handleTrackScroll}
      >
        {steps.map((step, index) => {
          const isTapped = index === tappedStep;
          const stepNum = String(index + 1).padStart(2, '0');

          return (
            <article
              key={step.title || index}
              className={`admissions-carousel-panel ${isTapped ? 'is-tapped' : ''}`}
              onClick={() => handlePanelClick(index)}
              onMouseLeave={handlePanelMouseLeave}
              tabIndex={0}
              role="button"
              aria-expanded={isTapped}
              aria-label={`Admissions step ${index + 1}: ${step.title || ''}`}
            >
              {step.image && (
                <div
                  className="admissions-carousel-image"
                  style={{ backgroundImage: `url(${step.image})` }}
                  aria-hidden="true"
                />
              )}
              <div className="admissions-carousel-overlay" aria-hidden="true" />
              <div className="admissions-carousel-content">
                <div className="admissions-step-top-row">
                  <span className="admissions-step-num-badge">Step {stepNum}</span>
                  <span className="admissions-step-hover-hint" aria-hidden="true">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </span>
                </div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            </article>
          );
        })}
      </div>

      {steps.length > 1 && (
        <div className="admissions-carousel-nav-wrap">
          <button
            type="button"
            className={`admissions-carousel-arrow left ${activeStep === 0 ? 'is-disabled' : ''}`}
            onClick={() => scrollToStep(activeStep - 1)}
            aria-label="Previous step"
            disabled={activeStep === 0}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <div className="admissions-carousel-dots">
            {steps.map((_, dotIdx) => (
              <button
                key={dotIdx}
                type="button"
                className={`admissions-carousel-dot ${dotIdx === activeStep ? 'active' : ''}`}
                onClick={() => scrollToStep(dotIdx)}
                aria-label={`Go to step ${dotIdx + 1}`}
              >
                <span className="carousel-dot-pill" />
              </button>
            ))}
          </div>

          <button
            type="button"
            className={`admissions-carousel-arrow right ${activeStep === steps.length - 1 ? 'is-disabled' : ''}`}
            onClick={() => scrollToStep(activeStep + 1)}
            aria-label="Next step"
            disabled={activeStep === steps.length - 1}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

function AdmissionsRequirementsSection({ requirements }) {
  const [activeTab, setActiveTab] = useState(0);

  if (!requirements || requirements.length === 0) return null;

  const currentTab = requirements[activeTab] || requirements[0] || { documents: [], eligibility: [] };
  const stageNum = String(activeTab + 1).padStart(2, '0');

  return (
    <div className="admissions-requirements-container">
      {/* Level Selector Tabs */}
      <div className="admissions-requirements-tabs" role="tablist" aria-label="Select Level Requirements">
        {requirements.map((item, index) => {
          const isActive = index === activeTab;
          const cleanLabel = (item.level || `Level ${index + 1}`).replace(/\s*admissions\s*$/i, '');
          return (
            <button
              key={item.level || index}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`admissions-requirements-tab ${isActive ? 'active' : ''}`}
              onClick={() => setActiveTab(index)}
            >
              <span className="req-tab-num">0{index + 1}</span>
              <span className="tab-label">{cleanLabel}</span>
            </button>
          );
        })}
      </div>

      <div className="admissions-requirements-content adm-animate is-visible">
        <div className="requirements-card">
          <div className="requirements-card-top-ribbon" aria-hidden="true" />

          <div className="requirements-header">
            <div className="requirements-stage-row">
              <span className="requirements-stage-pill">Stage {stageNum}</span>
              <span className="requirements-level-badge">{currentTab.level}</span>
            </div>
            <h4 className="requirements-card-title">{currentTab.level} Enrollment Criteria</h4>
            {currentTab.description && (
              <p className="requirements-description">{currentTab.description}</p>
            )}
          </div>

          <div className="requirements-grid">
            {currentTab.documents && currentTab.documents.length > 0 && (
              <div className="requirements-section">
                <div className="requirements-section-header">
                  <div className="section-icon-wrap docs">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                      <polyline points="10 9 9 9 8 9" />
                    </svg>
                  </div>
                  <h5>Required Documents</h5>
                </div>
                <ul className="requirements-list">
                  {currentTab.documents.map((doc, idx) => (
                    <li key={idx} className="requirement-item">
                      <span className="requirement-icon check">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </span>
                      <span className="requirement-text">{doc}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {currentTab.eligibility && currentTab.eligibility.length > 0 && (
              <div className="requirements-section">
                <div className="requirements-section-header">
                  <div className="section-icon-wrap elig">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 14 14" />
                    </svg>
                  </div>
                  <h5>Eligibility & Assessment</h5>
                </div>
                <ul className="requirements-list">
                  {currentTab.eligibility.map((item, idx) => (
                    <li key={idx} className="requirement-item">
                      <span className="requirement-icon arrow">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </span>
                      <span className="requirement-text">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="requirements-note">
            <div className="requirements-note-content">
              <div className="note-icon-wrap" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <p>
                <strong>Need Assistance?</strong> Contact our admissions team for clarification on document submission, translation requirements, or to schedule a consultation.
              </p>
            </div>
            <a className="requirements-help-cta" href="#online-application">
              <span>Contact Admissions</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function AdmissionsKeyDatesTimeline({ keyDates, header }) {
  if (!keyDates || keyDates.length === 0) return null;

  const getCardStatus = (item) => {
    if (item.status && ['active', 'completed', 'upcoming'].includes(String(item.status).toLowerCase())) {
      return String(item.status).toLowerCase();
    }
    const currentMonth = new Date().getMonth() + 1; // 1-12
    const monthMap = {
      'jan': 1, 'feb': 2, 'mar': 3, 'apr': 4,
      'may': 5, 'jun': 6, 'jul': 7, 'aug': 8,
      'sep': 9, 'oct': 10, 'nov': 11, 'dec': 12
    };
    const tagClean = String(item.tag || '').trim().toLowerCase().slice(0, 3);
    const cardMonth = monthMap[tagClean];
    if (!cardMonth) return 'upcoming';
    if (currentMonth === cardMonth) return 'active';
    if (currentMonth > cardMonth) return 'completed';
    return 'upcoming';
  };

  const getStatusLabel = (status) => {
    if (status === 'active') return 'Active Now';
    if (status === 'completed') return 'Completed';
    return 'Upcoming';
  };

  return (
    <section className="admissions-key-dates" id="admissions-key-dates" aria-label="Admissions Timeline & Key Dates">
      <div className="admissions-key-dates-shell">
        {/* Section Header */}
        <div className="key-dates-header adm-animate">
          <div className="key-dates-kicker-pill">
            <span className="key-dates-kicker-dot" aria-hidden="true" />
            <span>{header?.kicker || 'Timeline'}</span>
          </div>
          <h2 className="key-dates-title">{header?.title || 'Important Dates & Deadlines'}</h2>
          {header?.description && (
            <p className="key-dates-description">{header.description}</p>
          )}

          {/* Academic Year Legend Overview */}
          <div className="key-dates-overview-bar">
            <div className="key-dates-intake-badge">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <span>Intake Schedule 2026 / 2027</span>
            </div>

            <div className="key-dates-legend">
              <div className="legend-item completed">
                <span className="legend-dot" aria-hidden="true" />
                <span>Completed</span>
              </div>
              <div className="legend-item active">
                <span className="legend-dot" aria-hidden="true" />
                <span>Active Now</span>
              </div>
              <div className="legend-item upcoming">
                <span className="legend-dot" aria-hidden="true" />
                <span>Upcoming</span>
              </div>
            </div>
          </div>
        </div>

        {/* Timeline Grid */}
        <div className="key-dates-grid">
          {keyDates.map((item, index) => {
            const status = getCardStatus(item);
            const stageNumber = String(index + 1).padStart(2, '0');
            return (
              <article
                key={item.title || index}
                className={`key-date-card status-${status} adm-animate ${status === 'active' ? 'is-active-stage' : ''}`}
                style={{ '--stagger': index }}
              >
                {/* Top Status Accent Bar */}
                <div className="key-date-card-accent-bar" aria-hidden="true" />

                {/* Card Header: Stage Number & Month Pill */}
                <div className="key-date-card-top">
                  <div className="key-date-stage-badge">
                    <span className="key-date-stage-label">Stage</span>
                    <span className="key-date-stage-num">{stageNumber}</span>
                  </div>

                  <div className="key-date-top-meta">
                    {item.tag && (
                      <span className="key-date-month-pill">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12 6 12 12 16 14" />
                        </svg>
                        {item.tag}
                      </span>
                    )}

                    <div className={`key-date-status-pill ${status}`}>
                      {status === 'active' && <span className="status-pulse-dot" aria-hidden="true" />}
                      {status === 'completed' && (
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                      {status === 'upcoming' && (
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <circle cx="12" cy="12" r="10" />
                        </svg>
                      )}
                      <span>{getStatusLabel(status)}</span>
                    </div>
                  </div>
                </div>

                {/* Card Body */}
                <div className="key-date-card-body">
                  <h3 className="key-date-card-title">{item.title}</h3>

                  {(item.date || item.dateRange) && (
                    <div className="key-date-timeframe">
                      <div className="key-date-icon-box" aria-hidden="true">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                      </div>
                      <span className="key-date-timeframe-text">{item.date || item.dateRange}</span>
                    </div>
                  )}

                  {item.description && (
                    <p className="key-date-description">{item.description}</p>
                  )}
                </div>

                {/* Card Footer / Progress Milestone */}
                <div className="key-date-card-footer">
                  <span className="key-date-milestone-text">
                    Milestone {index + 1} of {keyDates.length}
                  </span>
                  <div className="key-date-footer-indicator" aria-hidden="true">
                    <span className="key-date-indicator-fill" />
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {/* Timeline Bottom Callout */}
        <div className="key-dates-helpdesk-card adm-animate">
          <div className="key-dates-helpdesk-content">
            <div className="key-dates-helpdesk-icon" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <div className="key-dates-helpdesk-text">
              <h4>Questions About Key Dates or Need Priority Consultation?</h4>
              <p>Our admissions team is available to help with scheduling assessments, document deadlines, and personalized campus visits.</p>
            </div>
          </div>
          <a className="key-dates-helpdesk-cta" href="#online-application">
            <span>Inquire Admissions</span>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}

function AdmissionsFeesPaymentSection({ feesList, header }) {
  if (!feesList || feesList.length === 0) return null;

  const getFallbackIcon = (iconName) => {
    const name = String(iconName || '').toLowerCase();
    if (name.includes('wallet') || name.includes('money') || name.includes('credit')) {
      return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="5" width="20" height="14" rx="2" />
          <line x1="2" y1="10" x2="22" y2="10" />
        </svg>
      );
    }
    if (name.includes('medal') || name.includes('award') || name.includes('trophy')) {
      return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="8" r="7" />
          <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
        </svg>
      );
    }
    if (name.includes('star')) {
      return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      );
    }
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
        <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
      </svg>
    );
  };

  return (
    <section className="fees-payment" id="fees-payment" aria-label="Fees & Payment Information">
      <div className="fees-payment-shell">
        {/* Header */}
        <div className="fees-payment-header adm-animate">
          <div className="fees-payment-kicker-pill">
            <span className="fees-kicker-dot" aria-hidden="true" />
            <span>{header?.kicker || 'Fees & Payment Information'}</span>
          </div>
          <h2 className="fees-payment-title">
            {header?.title || 'Transparent Fees, Flexible Plans & Clear Payment Options'}
          </h2>
          {header?.description && (
            <p className="fees-payment-description">{header.description}</p>
          )}

          {/* Quick Highlight Chips */}
          <div className="fees-payment-pillars">
            <span className="fees-pillar-chip">Transparent Pricing</span>
            <span className="fees-pillar-chip">Installment Options</span>
            <span className="fees-pillar-chip">Scholarship Guidance</span>
            <span className="fees-pillar-chip">No Hidden Fees</span>
          </div>
        </div>

        {/* 2x2 Grid on desktop, 1 col on mobile */}
        <div className="fees-payment-grid">
          {feesList.map((block, bIdx) => {
            const blockNum = String(bIdx + 1).padStart(2, '0');
            return (
              <article className="fees-payment-card adm-animate" key={block.title || bIdx} style={{ '--card-idx': bIdx }}>
                {/* Top Glowing Ribbon */}
                <div className="fees-card-ribbon" aria-hidden="true" />

                <div className="fees-card-top">
                  <div className="fees-payment-icon-wrapper">
                    <div className="fees-payment-icon" aria-hidden="true">
                      {getFallbackIcon(block.icon)}
                    </div>
                  </div>

                  <div className="fees-card-meta">
                    <span className="fees-card-num">Plan {blockNum}</span>
                  </div>
                </div>

                <div className="fees-card-body">
                  <h3 className="fees-card-title">{block.title}</h3>
                  {block.summary && (
                    <p className="fees-payment-summary">{block.summary}</p>
                  )}
                  {block.description && (
                    <p className="fees-payment-description">{block.description}</p>
                  )}

                  {block.items && block.items.length > 0 && (
                    <ul className="fees-payment-list">
                      {block.items.map((item, itemIdx) => (
                        <li key={itemIdx} className="fees-list-item">
                          <span className="fees-list-check" aria-hidden="true">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </span>
                          <span className="fees-list-text">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {block.highlight && (
                  <div className="fees-payment-highlight">
                    <div className="highlight-icon-box" aria-hidden="true">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                      </svg>
                    </div>
                    <p className="highlight-text">{block.highlight}</p>
                  </div>
                )}
              </article>
            );
          })}
        </div>

        {/* Bottom Consultation Assistance Banner */}
        <div className="fees-assistance-banner adm-animate">
          <div className="fees-assistance-content">
            <div className="fees-assistance-icon" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <div className="fees-assistance-text">
              <h4>Need Custom Installment Plans or Scholarship Advice?</h4>
              <p>Our financial advisory team is happy to discuss flexible payment terms, sibling discounts, and merit-based assistance.</p>
            </div>
          </div>
          <a className="fees-assistance-cta" href="#contact-form">
            <span>Contact Finance Desk</span>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}

function AdmissionsOnlineApplicationSection({ data }) {
  if (!data) return null;

  const {
    kicker = 'Online Admissions Portal',
    title = 'Online Application Form',
    summary = '',
    ctaLabel = 'Apply Online Now',
    ctaHref = '/register',
    note = '',
    image = '',
    highlights = []
  } = data;

  const displayImage = (image && !isVideoUrl(image)) ? image : defaultOnlineImage;

  const defaultHighlights = [
    '10-Minute Application',
    'Secure Document Upload',
    'Real-Time Status Tracking'
  ];

  const displayHighlights = Array.isArray(highlights) && highlights.length > 0 ? highlights : defaultHighlights;

  return (
    <section className="online-application" id="online-application">
      <div className="online-app-decor" aria-hidden="true">
        <div className="online-decor-glow-left" />
        <div className="online-decor-glow-right" />
      </div>

      <div className="online-app-shell">
        {/* Section Header */}
        <div className="online-application-header">
          <div className="online-application-kicker-wrap">
            <span className="online-kicker-sparkle" aria-hidden="true">✦</span>
            <span className="online-application-kicker">{kicker || 'Online Admissions Portal'}</span>
            <span className="online-kicker-sparkle" aria-hidden="true">✦</span>
          </div>

          <h2 className="online-application-title">
            {title || 'Begin Your Child\'s Journey of Excellence'}
          </h2>

          {summary && (
            <p className="online-application-summary">{summary}</p>
          )}
        </div>

        {/* Master Showcase Card */}
        <div className="online-portal-card adm-animate">
          {/* Top Gold Shimmer Line */}
          <div className="online-portal-top-bar" aria-hidden="true" />

          {/* Left Column: Information */}
          <div className="online-portal-info">
            <div className="online-portal-meta">
              <span className="online-badge-gold">Academic Year 2026 / 2027</span>
              <span className="online-badge-status">
                <span className="status-live-dot" aria-hidden="true" /> Portal Open
              </span>
            </div>

            <div className="online-portal-text-block">
              <h3 className="online-portal-headline">Apply Faster, Track Every Milestone</h3>
              <p className="online-portal-desc">
                {note || 'Complete your submission online in just a few minutes. Our admissions team will guide you through verification, student observation, and enrollment.'}
              </p>
            </div>

            {/* Quick 3-Step Process Line */}
            <div className="online-steps-timeline">
              <div className="online-step-node">
                <span className="step-node-num">01</span>
                <div className="step-node-text">
                  <strong>Fill Form</strong>
                  <span>Basic profile</span>
                </div>
              </div>
              <span className="step-node-divider" aria-hidden="true">→</span>
              <div className="online-step-node">
                <span className="step-node-num">02</span>
                <div className="step-node-text">
                  <strong>Upload Docs</strong>
                  <span>Report card & ID</span>
                </div>
              </div>
              <span className="step-node-divider" aria-hidden="true">→</span>
              <div className="online-step-node">
                <span className="step-node-num">03</span>
                <div className="step-node-text">
                  <strong>Assessment</strong>
                  <span>Join interview</span>
                </div>
              </div>
            </div>

            {/* Feature Highlights */}
            <div className="online-feature-chips">
              {displayHighlights.map((feat, fIdx) => (
                <div key={fIdx} className="online-feature-chip">
                  <svg className="chip-check-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>{typeof feat === 'object' ? feat.label : feat}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="online-portal-buttons">
              <a className="online-btn-gold" href={ctaHref || '/register'}>
                <span>{ctaLabel || 'Apply Online Now'}</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </a>

              <a className="online-btn-glass" href="/login">
                <span>Already Registered? Log In</span>
              </a>
            </div>

            {/* Encryption & Trust */}
            <div className="online-portal-security">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <span>256-bit SSL encrypted application submission</span>
            </div>
          </div>

          {/* Right Column: Proportional Photo Frame */}
          <div className="online-portal-photo-wrap">
            <div className="online-portal-photo-frame">
              <img
                src={displayImage}
                alt={title}
                className="online-portal-photo"
                loading="lazy"
                onError={(e) => {
                  if (e.target.src !== defaultOnlineImage) {
                    e.target.src = defaultOnlineImage;
                  }
                }}
              />
              <div className="online-photo-gradient-edge" aria-hidden="true" />

              {/* Clear, High-Contrast Floating Badge at Bottom */}
              <div className="online-photo-badge">
                <div className="photo-badge-icon" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <div className="photo-badge-details">
                  <strong>100% Online Enrollment</strong>
                  <span>Fast, Simple & Guided</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Consultation Banner */}
        <div className="online-help-strip adm-animate">
          <div className="help-strip-info">
            <div className="help-strip-icon" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
            </div>
            <div>
              <h4>Need Personal Assistance or Have Questions?</h4>
              <p>Our admissions counselors are available to assist via phone, WhatsApp, or schedule a personalized campus tour.</p>
            </div>
          </div>
          <a className="help-strip-cta" href="#contact-form">
            <span>Contact Admissions Desk</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}

export default AdmissionsPage;
